import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { Dispatch, ForwardedRef, forwardRef, SetStateAction } from 'react';
import { BaseFormState } from '~/types/BaseFormState';
import { SyncIcon } from '~/components/Icon/SyncIcon';
import styles from './SearchSelect.module.css';
import { FormState } from 'react-hook-form';
import { SelectProps } from '..';
import classNames from 'classnames';
import { get } from 'lodash';

export type OptionValue = string | number | null | undefined;
export type OptionTuple = [string, OptionValue] | [string, OptionValue, boolean];

export type Props<TFormState extends BaseFormState> = Omit<
  SelectProps<TFormState>,
  'value' | 'defaultValue' | 'onChange'
> & {
  renderOption?: (option: OptionTuple) => JSX.Element;
  onChange?: (value: NoInfer<OptionTuple>) => void;
  setSearch: Dispatch<SetStateAction<string>>;
  formState?: FormState<TFormState>;
  defaultValue?: OptionTuple;
  options: OptionTuple[];
  value?: OptionTuple;
  isLoading?: boolean;
  search?: string;
};

function SearchSelectElement<TFormState extends BaseFormState>(
  props: Props<TFormState>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const {
    value,
    options,
    placeholder,
    defaultValue,
    disabled,
    name,
    id,
    isLoading,
    formState,
    onChange,
    setSearch,
    renderOption,
  } = props;

  return (
    <Combobox
      onChange={value => value && onChange?.(value)}
      className={styles.searchSelect}
      onClose={() => setSearch('')}
      defaultValue={defaultValue}
      value={value}
      as="div"
    >
      <ComboboxInput
        className={classNames(styles.input, {
          [styles.error]: name && !!get(formState?.errors, name)?.message,
        })}
        onChange={event => setSearch(event.target.value)}
        displayValue={(v: OptionTuple) => v?.[0]}
        aria-label={props['aria-label'] ?? 'Sök'}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        name={name}
        ref={ref}
        id={id}
      />

      {isLoading && (
        <div className={styles.loadingContainer}>
          <SyncIcon className={styles.loading} />
        </div>
      )}

      <ComboboxOptions anchor="bottom" className={styles.options} transition>
        {options.map(([label, value, disabled = false]) => (
          <ComboboxOption key={value} value={[label, value]} disabled={disabled} className={styles.option}>
            {renderOption ? renderOption([label, value]) : label}
          </ComboboxOption>
        ))}
      </ComboboxOptions>
    </Combobox>
  );
}

export const SearchSelect = forwardRef(SearchSelectElement) as <TFormState extends BaseFormState>(
  props: Props<TFormState> & { ref?: React.ForwardedRef<HTMLInputElement> }
) => ReturnType<typeof SearchSelectElement>;
